import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

interface TextInputProps {
  text: string;
  modifyElement: (text: string) => void;
}

export const TextAreaExtendable: React.FC<TextInputProps> = props => {
  const { text, modifyElement } = props;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputText, setInputText] = useState<string>(text);

  // Keep callback in a ref so the debounced function stays stable across
  // re-renders — otherwise its cleanup cancels pending edits before they fire.
  const modifyElementRef = useRef(modifyElement);
  useEffect(() => {
    modifyElementRef.current = modifyElement;
  }, [modifyElement]);

  useEffect(() => {
    // Skip external sync while the user is editing — async renderer round-trips
    // would otherwise overwrite the in-progress edit and jump the cursor to the end.
    if (document.activeElement === inputRef.current) return;
    setInputText(text);
  }, [text]);

  const debouncedModify = useMemo(
    () => debounce((value: string) => modifyElementRef.current(value), 500),
    [],
  );

  useEffect(() => {
    return () => debouncedModify.cancel();
  }, [debouncedModify]);

  return (
    <TextArea
      value={inputText}
      ref={inputRef}
      placeholder="Enter Text"
      onChange={e => {
        setInputText(e.target.value);
        debouncedModify(e.target.value);
      }}
      onBlur={e => {
        debouncedModify.cancel();
        modifyElement(e.target.value);
      }}
      rows={1}
    />
  );
};

const TextArea = styled.textarea`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  box-sizing: border-box;
  display: block;
  padding: 12px 16px;
  align-self: stretch;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  text-align: left;
  line-height: 1.5;
  background-color: transparent;
  color: var(--text-secondary);
  width: 100%;
  resize: none;

  /* Auto-expand from 1 row to max 4 rows */
  field-sizing: content;
  min-height: 30px;
  max-height: 110px;
  overflow-y: auto;

  /*
   * Bordered-field-primitive hover/focus standard — see
   * docs/design-system.md "Form Fields (Bordered Primitives)".
   * Single channel: border-color escalates on hover and focus, no
   * outline halo. Keeps field chrome minimal on dense panels.
   */
  outline: none;
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: var(--text-muted);
  }
  &:focus-visible {
    border-color: var(--accent);
  }
`;
